import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CustomTableSortLabel } from '@components';
import TableFilterDropdown from '../../../../components/TableFilterDropdown';

// ---------------------------------------------------------------------------
// Shared style constants
// ---------------------------------------------------------------------------

export const cellTextSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: '1.25rem',
  textAlign: 'left',
};

export const dashboardLabelSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#162936',
};

export const dashboardTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: '#162C36',
      borderRadius: '0.5rem',
      padding: '0.75rem 1rem',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
      maxWidth: '280px',
      margin: '0 !important',
    },
  },
};

// ---------------------------------------------------------------------------
// Shared header renderers
// ---------------------------------------------------------------------------

export const renderCustomHeader = (label: string) => () => (
  <Box sx={{ width: '100%', textAlign: 'left', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
    {label}
  </Box>
);

type FilterOption = { label: string; value: string };

export const renderFilterableHeader =
  (label: string, options: FilterOption[], selected: string, onChange: (filter: string) => void) =>
  () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.25rem',
        width: '100%',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
      }}
    >
      {label}
      <TableFilterDropdown
        label={label}
        options={options}
        showSingleSelectIcon
        selected={selected ? [selected] : []}
        setSelected={(filters) => onChange(filters.length ? filters[filters.length - 1] : '')}
      />
    </Box>
  );

export const renderSortableHeader =
  (
    label: string,
    sortKey: string,
    sort: string,
    sortOrder: 'ASC' | 'DESC',
    onSort: (key: string) => void,
  ) =>
  () => (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      <CustomTableSortLabel
        label={label}
        active={sort === sortKey}
        sortDirection={sort === sortKey ? sortOrder : 'ASC'}
        onSort={() => onSort(sortKey)}
        labelStyles={{ fontSize: '0.875rem' }}
      />
    </Box>
  );

// ---------------------------------------------------------------------------
// Shared DashboardCell
// ---------------------------------------------------------------------------

/**
 * Structural subset of MultiLevelRowCommon consumed by DashboardCell.
 * Both ClientTableRow and PvxPartnerTableRow satisfy this shape.
 */
export interface DashboardCellRow {
  rowType: 'company' | 'user';
  hasFullDashboardAccess: boolean;
  totalEntitlementCount: number;
  dashboardSubmoduleNames: string[];
}

const dashboardTooltipListContent = (dashboards: string[]) => (
  <Box component="ul" sx={{ margin: 0, paddingLeft: '0.5rem' }}>
    {dashboards.map((dashboard) => (
      <Typography
        key={dashboard}
        component="li"
        sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#F6F8F9' }}
      >
        {dashboard}
      </Typography>
    ))}
  </Box>
);

const getTooltipDashboards = (row: DashboardCellRow): string[] => {
  if (row.hasFullDashboardAccess) {
    return Array.from(
      { length: row.totalEntitlementCount },
      (_, index) => row.dashboardSubmoduleNames[index] ?? '',
    );
  }
  return row.dashboardSubmoduleNames;
};

export const DashboardCell = ({ row }: { row: DashboardCellRow }) => {
  if (row.rowType === 'company') {
    return <Typography sx={cellTextSx}>-</Typography>;
  }

  const dashboards = getTooltipDashboards(row);
  const totalDashboards = row.totalEntitlementCount;
  const isAll = dashboards.length === totalDashboards;
  const label = isAll ? 'All' : `${dashboards.length}/${totalDashboards}`;

  const tooltipTitle = (() => {
    if (isAll) {
      const names = row.dashboardSubmoduleNames.filter(Boolean);
      return names.length > 0 ? dashboardTooltipListContent(names) : 'All dashboards';
    }
    if (dashboards.length === 0) return null;
    return dashboardTooltipListContent(row.dashboardSubmoduleNames);
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.25rem',
      }}
    >
      <Typography sx={dashboardLabelSx}>{label}</Typography>
      {tooltipTitle && (
        <Tooltip
          title={tooltipTitle}
          placement="bottom-start"
          slotProps={dashboardTooltipSlotProps}
        >
          <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#2F736E', cursor: 'pointer' }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Shared actions cell
// ---------------------------------------------------------------------------

export interface ActionsCellRow {
  rowType: 'company' | 'user';
}

export const renderActionsCell = <T extends ActionsCellRow>(
  row: T,
  onEditCompany: (row: T) => void,
  onActionClick: (event: React.MouseEvent<HTMLElement>, row: T) => void,
) => {
  if (row.rowType === 'company') {
    return (
      <Typography
        component="button"
        onClick={(event) => {
          event.stopPropagation();
          onEditCompany(row);
        }}
        sx={{
          fontSize: '0.875rem',
          fontWeight: 700,
          color: '#51B8B0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'left',
        }}
      >
        Edit Access
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <IconButton
        disableFocusRipple
        disableRipple
        disableTouchRipple
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onActionClick(event, row);
        }}
      >
        <MoreVertIcon />
      </IconButton>
    </Box>
  );
};
